import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, X, Globe, Check, Phone, Download } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { CONTACT } from '@/data/contact'
import { useModal } from '@/context/ModalContext'
import { MARKET } from '@/market'

const LANGUAGES = MARKET.languages

/** Nav entries, in page order. Each `id` is a real section anchor. */
const NAV_ITEMS = [
  { id: 'how', labelKey: 'nav.how' },
  { id: 'features', labelKey: 'nav.features' },
  { id: 'pricing', labelKey: 'nav.pricing' },
  { id: 'integrations', labelKey: 'nav.integrations' },
] as const

const MOBILE_MENU_ID = 'mobile-nav'

export function Header() {
  const { t, i18n } = useTranslation()
  const { open: openContact } = useModal()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) ?? LANGUAGES[0]

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /** Highlight whichever section currently occupies the middle of the viewport. */
  useEffect(() => {
    const sections = NAV_ITEMS.map(item => document.getElementById(item.id)).filter(
      (el): el is HTMLElement => el !== null
    )
    if (!sections.length) return

    const observer = new IntersectionObserver(
      entries => {
        const best = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (best) setActiveId(best.target.id)
      },
      { rootMargin: '-25% 0px -55% 0px', threshold: [0, 0.2, 0.5, 1] }
    )

    sections.forEach(section => observer.observe(section))
    return () => observer.disconnect()
  }, [])

  /* ── Mobile menu behaviour ──────────────────────────────────── */

  // Lock background scrolling so the page doesn't slide behind the panel.
  useEffect(() => {
    if (!mobileOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [mobileOpen])

  useEffect(() => {
    if (!mobileOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [mobileOpen])

  // Growing past the mobile breakpoint would otherwise leave the panel stuck open.
  useEffect(() => {
    if (!mobileOpen) return
    const query = window.matchMedia('(min-width: 768px)')
    const onChange = () => {
      if (query.matches) setMobileOpen(false)
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [mobileOpen])

  const closeMenu = () => setMobileOpen(false)

  // The scroll lock above swallows an anchor jump made while the panel is open,
  // so menu links close the panel first and scroll once the lock is released.
  const pendingAnchor = useRef<string | null>(null)
  useEffect(() => {
    if (mobileOpen || !pendingAnchor.current) return
    const id = pendingAnchor.current
    pendingAnchor.current = null
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView()
      history.replaceState(null, '', `#${id}`)
    }, 0)
    return () => window.clearTimeout(timer)
  }, [mobileOpen])

  const goFromMenu = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    pendingAnchor.current = id
    closeMenu()
  }

  const focusRing =
    'outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-dark-bg'

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-colors duration-300',
        // Solid whenever it needs to be readable: scrolled, or the panel is open.
        scrolled || mobileOpen
          ? 'bg-dark-bg/95 backdrop-blur-md border-b border-dark-border shadow-lg shadow-black/20'
          : 'bg-transparent'
      )}
    >
      <div className="relative z-10 mx-auto flex h-16 max-w-[1200px] items-center justify-between px-4 sm:px-6">
        {/* Logo */}
        <a
          href="#top"
          onClick={closeMenu}
          aria-label="Samly POS"
          className={cn('flex select-none items-center gap-1.5 rounded-lg', focusRing)}
        >
          <img src="/SAMLY_LOGO.png" alt="Samly" className="h-8 w-auto" />
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
            POS
          </span>
        </a>

        {/* Desktop nav */}
        <nav aria-label={t('nav.primary_nav')} className="hidden md:flex items-center gap-7">
          {NAV_ITEMS.map(item => {
            const active = activeId === item.id
            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                aria-current={active ? 'true' : undefined}
                className={cn(
                  'relative rounded py-1 text-[14px] font-medium transition-colors',
                  active ? 'text-white' : 'text-white/65 hover:text-white',
                  focusRing
                )}
              >
                {t(item.labelKey)}
                {active && (
                  <motion.span
                    layoutId="nav-active"
                    className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded-full bg-primary"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </a>
            )
          })}
        </nav>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-2.5">
          <a
            href={CONTACT.phone.href}
            className={cn(
              'hidden lg:inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-1 text-[14px] font-semibold text-white/80 transition-colors hover:text-white',
              focusRing
            )}
          >
            <Phone className="h-3.5 w-3.5 shrink-0" />
            {CONTACT.phone.display}
          </a>

          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={currentLang.label}
              className={cn(
                'flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-[13px] font-semibold text-white/80 transition-colors hover:bg-white/20 hover:text-white',
                focusRing
              )}
            >
              <Globe className="h-3.5 w-3.5 shrink-0" />
              {currentLang.short}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[130px]">
              {LANGUAGES.map(lang => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={cn(
                    'justify-between',
                    i18n.language === lang.code && 'text-primary hover:text-primary'
                  )}
                >
                  <span>{lang.label}</span>
                  {i18n.language === lang.code && <Check className="h-3.5 w-3.5 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Secondary to the contact CTA: scrolls to the apps section, where the visitor picks POS or a mobile app. */}
          <a
            href="#apps"
            aria-label={t('nav.download')}
            className={cn(
              'hidden h-11 items-center gap-1.5 whitespace-nowrap rounded-lg bg-white/10 px-3.5 text-[14px] font-semibold text-white/85 transition-colors hover:bg-white/20 hover:text-white lg:inline-flex',
              focusRing
            )}
          >
            <Download className="h-4 w-4 shrink-0" />
            {/* From lg as an icon, label from xl: below that the Georgian bar overflows. */}
            <span className="hidden xl:inline">{t('nav.download')}</span>
          </a>

          {/* Same conversion path as every other CTA on the page */}
          <button
            onClick={openContact}
            className={cn(
              'inline-flex h-11 items-center justify-center rounded-lg bg-primary px-5 text-[14px] font-semibold text-white transition-colors hover:bg-primary-dark',
              focusRing
            )}
          >
            {t('nav.cta')}
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className={cn('rounded-lg p-2 text-white transition-colors hover:bg-white/10 md:hidden', focusRing)}
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label={t('nav.menu')}
          aria-expanded={mobileOpen}
          aria-controls={MOBILE_MENU_ID}
        >
          <motion.span
            className="block"
            animate={{ rotate: mobileOpen ? 90 : 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </motion.span>
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Tap anywhere below the panel to dismiss */}
            <motion.div
              key="mobile-scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeMenu}
              aria-hidden="true"
              className="fixed inset-x-0 bottom-0 top-16 z-0 bg-black/60 md:hidden"
            />

            <motion.div
              key="mobile-menu"
              id={MOBILE_MENU_ID}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              /* Fully opaque on purpose. This previously used a 98% opacity
                 modifier, which is not a step on Tailwind's opacity scale, so
                 it compiled to no background at all and the panel was
                 see-through. Keep opacity modifiers on multiples of 5. */
              className="relative z-10 overflow-hidden border-t border-dark-border bg-dark-bg md:hidden"
            >
              <div className="max-h-[calc(100dvh-4rem)] overflow-y-auto px-4 pb-6 sm:px-6">
                <nav className="flex flex-col pt-1">
                  {NAV_ITEMS.map((item, i) => (
                    <motion.a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={goFromMenu(item.id)}
                      aria-current={activeId === item.id ? 'true' : undefined}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.06 + i * 0.05, duration: 0.22 }}
                      className={cn(
                        'flex items-center justify-between border-b border-dark-border/60 py-3.5 text-[15px] font-medium transition-colors last:border-0',
                        activeId === item.id ? 'text-primary' : 'text-white/75 hover:text-white'
                      )}
                    >
                      {t(item.labelKey)}
                      {activeId === item.id && (
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                      )}
                    </motion.a>
                  ))}
                  <motion.a
                    href="#apps"
                    onClick={goFromMenu('apps')}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 + NAV_ITEMS.length * 0.05, duration: 0.22 }}
                    className="flex items-center justify-between py-3.5 text-[15px] font-medium text-white/75 transition-colors hover:text-white"
                  >
                    {t('nav.apps')}
                    <Download className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  </motion.a>
                </nav>

                {/* Primary action first, so the thumb lands on it */}
                <motion.button
                  onClick={() => {
                    closeMenu()
                    openContact()
                  }}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-5 flex h-12 w-full items-center justify-center rounded-lg bg-primary text-[15px] font-semibold text-white transition-colors hover:bg-primary-dark"
                >
                  {t('nav.cta')}
                </motion.button>

                <motion.a
                  href="#apps"
                  onClick={goFromMenu('apps')}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.22 }}
                  className="mt-2.5 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-white/10 text-[15px] font-semibold text-white transition-colors hover:bg-white/20"
                >
                  <Download className="h-4 w-4 shrink-0" />
                  {t('nav.download')}
                </motion.a>

                <motion.a
                  href={CONTACT.phone.href}
                  onClick={closeMenu}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.24 }}
                  className="mt-2.5 flex h-12 w-full items-center justify-center gap-2 rounded-lg border border-white/20 text-[15px] font-semibold text-white transition-colors hover:bg-white/10"
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  {CONTACT.phone.display}
                </motion.a>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.28 }}
                  className="mt-3 text-center text-[12px] text-white/35"
                >
                  {t('contact.hours')}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-5 flex items-center gap-2 border-t border-dark-border pt-5"
                >
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => i18n.changeLanguage(lang.code)}
                      aria-pressed={i18n.language === lang.code}
                      className={cn(
                        'rounded-lg px-3.5 py-2 text-[13px] font-semibold transition-colors',
                        i18n.language === lang.code
                          ? 'bg-primary text-white'
                          : 'bg-white/10 text-white/60 hover:text-white'
                      )}
                    >
                      {lang.short}
                    </button>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  )
}
